import React, {
  useCallback,
  useEffect,
  useRef,
  useState,
  useMemo,
} from 'react';
import { Field, getFormValues, reduxForm } from 'redux-form';
import { Row, Col, Container } from 'reactstrap';
import TimetableIcon from 'mdi-react/TimetableIcon';
import moment from 'moment';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import renderField from '../../../shared/components/custom/Field';
import validate from './validateTileForm';
import t, { partial } from '../../../util/translation/translation';
import renderDropZoneField from '../../../shared/components/form/DropZoneMS';
import renderSelectField from '../../../shared/components/form/Select';
import Api from '../../../util/api';
import useConfig from '../../../util/useConfig';
import FormBox from '../../../shared/components/custom/FormBox';
import TooltipWithIcon from '../../../shared/components/custom/TooltipWithIcon';
import {
  getReadableFormat,
  getFormatDimensions,
  isFilterable,
  hasAccess,
} from '../spotlightUtils';
import { getImageHash } from '../../../util/functions';
import NewspaperIcon from 'mdi-react/NewspaperIcon';
import ShapeOutlineIcon from 'mdi-react/ShapeOutlineIcon';
import ClipboardTextOutlineIcon from 'mdi-react/ClipboardTextOutlineIcon';
import TrophyOutlineIcon from 'mdi-react/TrophyOutlineIcon';
import SoccerIcon from 'mdi-react/SoccerIcon';
import StarOutlineIcon from 'mdi-react/StarOutlineIcon';
import AccountOutlineIcon from 'mdi-react/AccountOutlineIcon';
import AccountGroupOutlineIcon from 'mdi-react/AccountGroupOutlineIcon';
import TagOutlineIcon from 'mdi-react/TagOutlineIcon';
import CalendarOutlineIcon from 'mdi-react/CalendarOutlineIcon';
import GavelIcon from 'mdi-react/GavelIcon';
import DomainIcon from 'mdi-react/DomainIcon';
import LinkVariantIcon from 'mdi-react/LinkVariantIcon';
import WebIcon from 'mdi-react/WebIcon';
import YoutubeIcon from 'mdi-react/YoutubeIcon';
import VideoOutlineIcon from 'mdi-react/VideoOutlineIcon';
import ImageOutlineIcon from 'mdi-react/ImageOutlineIcon';
import SpotlightBeamIcon from 'mdi-react/SpotlightBeamIcon';
import CardTextOutlineIcon from 'mdi-react/CardTextOutlineIcon';
import renderDropZoneVideoField from '../../../shared/components/form/DropZoneVideo';
import ChoiceCards from '../../../shared/components/custom/ChoiceCards';
import OptionalColorField from '../../../shared/components/form/OptionalColorField';
import renderTextAreaField from '../../../shared/components/custom/TextArea';
import {
  DARK_CLUB_LIGHT,
  TAKEOVER_BODY_MAX,
  isGenericTakeover,
  presentationToForm,
} from '../takeoverUtils';

const UNLIMITED_DATE = '9999-12-31T00:00:00.000Z';

const p = partial('HighlightBannerForm');
const bp = partial('HighlightBannerPage');
const s = partial('shared');
// Labels a tile can show on its image
const TILE_BADGES = ['NEW', 'LIVE', 'SPONSORED', 'AD'];
const AVAILABLE_CONTENT = [
  {
    value: 'NEWS',
    label: s('news'),
  },
  {
    value: 'OFFERS',
    label: t('AppLayout.offersTile'),
  },
  {
    value: 'EVENTS',
    label: t('AppLayout.eventsTile'),
  },
  {
    value: 'EXTERNAL_LINK',
    label: s('externalLink'),
  },
  {
    value: 'WEBVIEW_LINK',
    label: s('webViewLink'),
  },
  {
    value: 'YOUTUBE_VIDEO',
    label: t('NewsForm.YOUTUBE_VIDEO_title'),
  },
  {
    value: 'CUSTOM_VIDEO',
    label: t('NewsForm.CUSTOM_VIDEO_title'),
  },
  {
    value: 'AUCTIONS',
    label: t('AppLayout.auctionsTile'),
  },
  {
    value: 'COMPANIES',
    label: s('companies'),
  },
  {
    value: 'PLAYERS',
    label: s('players'),
  },
  {
    value: 'NEWS_CATEGORY',
    label: s('newsCategory'),
  },
  {
    value: 'TEAMS',
    label: s('teams'),
  },
  {
    value: 'QUESTIONAIRES',
    label: t('questionaire.questionaires'),
  },
  {
    value: 'QUESTIONAIRE_CATEGORIES_LEADERBOARD',
    label: t(
      'questionaire.questionaireCategories.questionaireCategoriesLeaderboard'
    ),
  },
  {
    value: 'MATCHES',
    label: s('match'),
  },
  {
    value: 'MATCH_MVPS',
    label: s('MVP'),
  },
  {
    value: 'GOAL_SPONSOR',
    label: t('goalSponsor.goalSponsor'),
  },
  {
    value: 'IMAGE',
    label: t('HighlightBannerForm.imageTile'),
  },
];
// The Select content dropdown: types in groups, each with an icon
const CONTENT_GROUPS = [
  {
    key: 'links',
    types: [
      'EXTERNAL_LINK',
      'WEBVIEW_LINK',
      'YOUTUBE_VIDEO',
      'CUSTOM_VIDEO',
      'IMAGE',
    ],
  },
  {
    key: 'news',
    types: [
      'NEWS',
      'NEWS_CATEGORY',
      'QUESTIONAIRES',
      'QUESTIONAIRE_CATEGORIES_LEADERBOARD',
    ],
  },
  { key: 'matches', types: ['MATCHES', 'MATCH_MVPS', 'PLAYERS', 'TEAMS'] },
  {
    key: 'partners',
    types: ['OFFERS', 'EVENTS', 'AUCTIONS', 'COMPANIES', 'GOAL_SPONSOR'],
  },
];
const CONTENT_ICONS = {
  NEWS: NewspaperIcon,
  NEWS_CATEGORY: ShapeOutlineIcon,
  QUESTIONAIRES: ClipboardTextOutlineIcon,
  QUESTIONAIRE_CATEGORIES_LEADERBOARD: TrophyOutlineIcon,
  MATCHES: SoccerIcon,
  MATCH_MVPS: StarOutlineIcon,
  PLAYERS: AccountOutlineIcon,
  TEAMS: AccountGroupOutlineIcon,
  OFFERS: TagOutlineIcon,
  EVENTS: CalendarOutlineIcon,
  AUCTIONS: GavelIcon,
  COMPANIES: DomainIcon,
  GOAL_SPONSOR: StarOutlineIcon,
  EXTERNAL_LINK: LinkVariantIcon,
  WEBVIEW_LINK: WebIcon,
  YOUTUBE_VIDEO: YoutubeIcon,
  CUSTOM_VIDEO: VideoOutlineIcon,
  IMAGE: ImageOutlineIcon,
};
const renderContentOption = (option) => {
  if (option.isGroup) {
    return <span className="content-option__group">{option.label}</span>;
  }
  const Icon = CONTENT_ICONS[option.value];
  return (
    <span className="content-option">
      {Icon && <Icon size={16} />}
      {option.label}
    </span>
  );
};
// Typing searches the types (headings hidden); nothing typed: everything
const filterContentOptions = (options, filter) => {
  const query = `${filter || ''}`.trim().toLowerCase();
  if (!query) return options;
  return options.filter(
    (option) =>
      !option.isGroup && `${option.label}`.toLowerCase().includes(query)
  );
};

const getContentType = (type) => {
  return AVAILABLE_CONTENT.find((item) => item.value === type);
};
const getCropType = (format, size) => {
  // A takeover's image (a cut-out or a card) keeps its own shape
  if (format === 'TAKEOVER') {
    return 'FREE_CROP';
  }
  if (format === 'BANNER') {
    return `SPOTLIGHT_${format}_${size}_CROP`;
  }
  if (format === 'TEXT_BANNER') {
    return 'SPOTLIGHT_CIRCLE_CROP';
  }
  return `SPOTLIGHT_${format}_CROP`;
};
const EMPTY_TEAMS = [];
// Image style cards: a figure cut out of its background, or a whole card
const CutoutIcon = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
    <circle cx="12" cy="7" r="4" />
    <path d="M4 22 C 4.5 15, 8 13, 12 13 C 16 13, 19.5 15, 20 22 Z" />
  </svg>
);
const CardIcon = ({ size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
    <rect x="5" y="2" width="14" height="20" rx="2" opacity="0.35" />
    <circle cx="12" cy="9" r="3" />
    <rect x="8" y="15" width="8" height="2" rx="1" />
  </svg>
);
// Quick picks for the light: cool white, warm gold, white
const LIGHT_PRESETS = [DARK_CLUB_LIGHT, '#F2C14E', '#FFFFFF'];

const SpotlightTileForm = ({
  handleSubmit,
  initialize,
  entity,
  change,
  format,
  size,
  setSelectedEntityAccess,
  groupsLevelsTiers,
  descriptionAvailable,
  formValues,
  onLoadingChange,
}) => {
  const [loading, setLoading] = useState(false);
  const [contentLoaded, setContentLoaded] = useState(false);
  const [contentTypes, setContentTypes] = useState([]);
  const [auctionItems, setAuctionItems] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [content, setContent] = useState(null);
  const initializedEntityKeyRef = useRef(null);
  const { id: clubId, teams: configTeams } = useConfig();
  const teams = configTeams || EMPTY_TEAMS;
  const teamIds = useMemo(() => teams.map((team) => team.id), [teams]);
  const teamsKey = useMemo(() => teamIds.join(','), [teamIds]);
  const teamContentOptions = useMemo(
    () =>
      teams.map((item) => ({
        label: item.name,
        value: item.id,
      })),
    [teams]
  );
  const imageFormat = format || 'SQUARE';
  const imageSize = size || 'MEDIUM';
  // A takeover shows no description, and has a section of its own
  const isTakeover = format === 'TAKEOVER';
  const showDescription = descriptionAvailable && !isTakeover;
  // The image-and-text takeover: its description is the text, and what only
  // the spotlight shows (first line, facts, number, image style, light) hides
  const isGeneric = isTakeover && isGenericTakeover(formValues?.presentation);
  const CONTENT_LIST = useMemo(() => {
    // YouTube tiles only in portrait and landscape groups
    const offered = AVAILABLE_CONTENT.filter(
      (item) =>
        item.value !== 'YOUTUBE_VIDEO' ||
        ['PORTRAIT', 'LANDSCAPE'].includes(format)
    );
    const grouped = CONTENT_GROUPS.flatMap((group) => {
      const items = offered
        .filter((item) => group.types.includes(item.value))
        .sort((a, b) => `${a.label}`.localeCompare(`${b.label}`));
      return items.length
        ? [
            {
              value: `group-${group.key}`,
              label: p(`contentGroup-${group.key}`),
              isGroup: true,
              disabled: true,
            },
            ...items,
          ]
        : [];
    });
    // Any type not in a group still shows, at the end
    const rest = offered.filter(
      (item) =>
        !CONTENT_GROUPS.some((group) => group.types.includes(item.value))
    );
    return [...grouped, ...rest];
  }, [format]);
  // The options for one content type, fetched only when that type is used
  // (opening a tile, or picking a type) and then kept in contentCache
  const fetchContentOptions = useCallback(
    async (key) => {
      const listItem = (label) => ({
        value: null,
        label: `${label} ${s('list')}`,
      });
      const toOptions = (items, label = 'title') =>
        items.map((item) => ({ label: item[label], value: item.id }));
      const fetchMatches = async () => {
        const {
          data: { matches },
        } = await Api.matchCenter.getUpcomingMatches(1, 100, teamIds, false);
        // The value carries which of our teams plays
        return matches.map((item) => {
          const foundTeamId = teamIds.find(
            (teamId) =>
              teamId === item?.homeTeam?.id || teamId === item?.awayTeam?.id
          );
          return {
            label: `${item?.league?.name} - ${item?.homeTeam?.name} - ${item?.awayTeam?.name}`,
            value: `${foundTeamId}@${item.id}`,
          };
        });
      };
      switch (key) {
        case 'news': {
          const { data } = await Api.news.getNews(
            { page: 1, take: 100 },
            { orderBy: 'publicationDate', direction: 'DESC' }
          );
          return [listItem(t('AppLayout.newsTile')), ...toOptions(data)];
        }
        case 'events': {
          const { data } = await Api.events.all();
          const sorted = data.sort((a, b) =>
            moment(b.startDate).diff(moment(a.startDate))
          );
          return [listItem(t('AppLayout.eventsTile')), ...toOptions(sorted)];
        }
        case 'offers': {
          const { data } = await Api.offers.fetchAllOffers(clubId, 1, 50);
          return [
            listItem(t('AppLayout.offersTile')),
            ...toOptions(data.offers),
          ];
        }
        case 'auctions': {
          const { data } = await Api.auctions.getAuctionHouses(
            { page: 1, take: 100 },
            { orderBy: 'createdAt', direction: 'DESC' }
          );
          return [
            listItem(t('auctionObjectsShared.auctionHouse')),
            ...toOptions(data, 'name'),
          ];
        }
        case 'companies': {
          const resCompanies = await Api.companies.getCompaniesForClub();
          const sorted = resCompanies
            .filter((item) => item.company.name)
            .sort((a, b) =>
              a.company.name.trim().localeCompare(b.company.name.trim())
            );
          return [
            listItem(s('companies')),
            ...sorted.map((item) => ({
              label: item.company.name,
              value: item.id,
            })),
          ];
        }
        case 'news_category':
          return toOptions(await Api.news.getNewsCategories(), 'name');
        case 'players': {
          const teamPlayers = await Promise.all(
            teamIds.map((teamId) =>
              Api.players
                .getPLayersFromNtb(teamId)
                .then((res) => res.data.map((item) => ({ ...item, teamId })))
            )
          );
          return teamPlayers.flat().map((item) => ({
            label: item.fullname,
            value: `${item.teamId}@${item.id}`,
          }));
        }
        case 'teams':
          return teamContentOptions;
        case 'questionaires':
          return toOptions(await Api.questionaire.getQuestionaireOptions());
        case 'questionaire_categories_leaderboard':
          return toOptions(
            await Api.questionaire.categories.getQuestionaireCategories()
          );
        case 'matches':
          return fetchMatches();
        case 'match_mvps':
          return (await fetchMatches()).map((item) => ({
            ...item,
            value: `${item.value}@MVP`,
          }));
        case 'goal_sponsor': {
          const { data } =
            await Api.goalSponsor.subscriptionPlans.getSubscriptionPlans(
              { page: 1, take: 100 },
              { orderBy: 'createdAt', direction: 'DESC' }
            );
          return toOptions(data, 'name');
        }
        default:
          return [];
      }
    },
    [clubId, teamIds, teamContentOptions]
  );
  const contentCache = useRef({});
  const latestContentType = useRef(null);
  useEffect(() => {
    // Club or teams changed: the kept lists are someone else's
    contentCache.current = {};
  }, [fetchContentOptions]);
  // A content type's options: kept ones, else fetched now (once)
  const loadContent = useCallback(
    async (key) => {
      if (contentCache.current[key]) {
        return contentCache.current[key];
      }
      try {
        const options = await fetchContentOptions(key);
        contentCache.current[key] = options;
        return options;
      } catch (e) {
        toast.error(e || s('somethingWentWrong'));
        return [];
      }
    },
    [fetchContentOptions]
  );
  const mapOfferAccess = useCallback((accessArray, field) => {
    let key = null;
    switch (field) {
      case 'groups':
        key = 'name';
        break;
      case 'levels':
        key = 'name';
        break;
      case 'tiers':
        key = 'tierId';
        break;
      default:
        break;
    }

    return accessArray.length === 0 ||
      accessArray?.find((item) => item.name === 'All users')
      ? null
      : accessArray.find((item) => item.name === `All ${field}`)
        ? []
        : accessArray.map((item) => item[key]);
  }, []);

  const mapEventAccess = useCallback(
    (accessArray, field, searchField) => {
      const mappedData =
        accessArray === null || accessArray?.length === 0
          ? accessArray
          : accessArray.map((accessName) => {
              const foundRecord = groupsLevelsTiers[field].find(
                (item) => item[searchField] === accessName
              );
              return foundRecord?.value;
            });
      return mappedData;
    },
    [groupsLevelsTiers]
  );
  const fetchEntityAccess = useCallback(
    async (entityId, contentType) => {
      switch (contentType) {
        case 'NEWS':
          return Api.news.getNewsAccess(entityId);
        case 'QUESTIONAIRES':
          return Api.questionaire.getQuestionaireAccess(entityId);
        case 'OFFERS':
          return Api.offers
            .fetchSingleOffer(entityId, clubId)
            .then(({ data }) => {
              const groups = mapOfferAccess(data.groups, 'groups');
              const levels = mapOfferAccess(data.levels, 'levels');
              const tiers = mapOfferAccess(data.tiers, 'tiers');
              return { groups, levels, tiers, noAccessView: data.isVisible };
            });
        case 'EVENTS':
          return Api.events.show(entityId).then(({ data }) => {
            const groups = mapEventAccess(
              data.accessCategories,
              'groups',
              'value'
            );
            const levels = mapEventAccess(data.accessLevels, 'levels', 'label');
            const tiers = mapEventAccess(data.accessTiers, 'tiers', 'value');
            return { groups, levels, tiers, noAccessView: data.feedVisibility };
          });
        case 'AUCTIONS': {
          const [houseId] = entityId.split('|');
          const [auctionHouseAccess, auctionItemsData] = await Promise.all([
            Api.auctions.getAuctionHouseAccess(houseId),
            Api.auctions.getAuctionItemsForHouse(houseId),
          ]);
          const mappedItems = [
            {
              label: 'Main page',
              value: null,
            },
            ...auctionItemsData.map((item) => ({
              label: item.name,
              value: item.id,
            })),
          ];
          setAuctionItems(mappedItems);
          return auctionHouseAccess;
        }
        default:
          return null;
      }
    },
    [clubId, mapEventAccess, mapOfferAccess]
  );
  const handleContentTypeChange = useCallback(
    async (data, contentType) => {
      if (hasAccess(contentType)) {
        const entityId = data?.value;
        if (!entityId) {
          setSelectedEntityAccess({
            groups: null,
            levels: null,
            tiers: null,
            segments: [],
            noAccessView: true,
            selectedContent: { type: contentType, contentId: entityId },
          });
        } else {
          const result = await fetchEntityAccess(entityId, contentType);
          if (!result) {
            setSelectedEntityAccess({
              groups: null,
              levels: null,
              tiers: null,
              segments: [],
              noAccessView: true,
              selectedContent: { type: contentType, contentId: entityId },
            });
            return;
          }
          const { groups, levels, tiers, noAccessView } = result;
          setSelectedEntityAccess({
            groups,
            levels,
            tiers,
            segments: [],
            noAccessView,
            selectedContent: { type: contentType, contentId: entityId },
          });
        }
      } else {
        setSelectedEntityAccess({
          groups: null,
          levels: null,
          tiers: null,
          segments: [],
          noAccessView: true,
          selectedContent: { type: contentType, contentId: data?.value },
          isEditable: true,
        });
      }
    },
    [fetchEntityAccess, setSelectedEntityAccess]
  );
  const entityInitializationKey = entity
    ? `${entity.id || entity.tileId || ''}-${clubId || ''}-${teamsKey}-${format || ''}`
    : null;
  useEffect(() => {
    if (!entity || !contentLoaded) {
      return undefined;
    }
    if (initializedEntityKeyRef.current === entityInitializationKey) {
      return undefined;
    }

    let cancelled = false;
    const initializeEntity = async () => {
      let foundContent = entity.contentId || '';
      let foundSubContent = null;
      const selectedCompany = entity.companyId
        ? companies.find((com) => com.value === entity.companyId)
        : null;
      setLoading(true);
      if (isFilterable(entity.contentType)) {
        const contentOptions = await loadContent(
          entity.contentType.toLowerCase()
        );
        if (entity.contentType === 'AUCTIONS') {
          const [houseId, itemId] = foundContent.split('|');
          const auctionHouseItems =
            await Api.auctions.getAuctionItemsForHouse(houseId);
          const mappedAuctionHouseItems = [
            {
              label: bp('auctionHousesPage'),
              value: null,
            },
            ...auctionHouseItems.map((item) => ({
              label: item.name,
              value: item.id,
            })),
          ];

          if (cancelled) {
            return;
          }

          setAuctionItems(mappedAuctionHouseItems);
          foundContent =
            contentOptions.find((item) => item.value === houseId)?.label ||
            contentOptions.find((item) => item.value === null)?.label;
          const isAuctionHouseList =
            foundContent ===
            `${t('auctionObjectsShared.auctionHouse')} ${s('list')}`;
          foundSubContent = isAuctionHouseList
            ? null
            : {
                label:
                  mappedAuctionHouseItems.find((item) => item.value === itemId)
                    ?.label ||
                  contentOptions.find((item) => item.value === null)?.label,
                value: itemId,
              };
        } else {
          foundContent =
            contentOptions.find((item) => item.value === entity.contentId)
              ?.label ||
            contentOptions.find((item) => item.value === null)?.label;
        }
      }

      if (cancelled) {
        return;
      }

      initialize({
        title: entity.title,
        description: entity.description,
        publicationDate: moment(entity.publicationDate).format(
          'YYYY-MM-DDTHH:mm'
        ),
        expirationDate:
          entity.expirationDate !== UNLIMITED_DATE
            ? moment(entity.expirationDate).format('YYYY-MM-DDTHH:mm')
            : null,
        content: getContentType(entity.contentType),
        contentType: isFilterable(entity.contentType)
          ? {
              label: foundContent,
              value: entity.contentId,
            }
          : entity.contentId,
        subContentType: foundSubContent,
        headingImage:
          format === undefined
            ? undefined
            : {
                name: 'tile image',
                preview: entity.imageUrl,
                croppedImage: entity.imageUrl,
                hash: getImageHash(entity.imageUrl),
              },
        headingVideo: entity.contentId
          ? {
              preview: entity.contentId,
            }
          : null,
        company: selectedCompany,
        highlight: entity.highlight || '',
        // A takeover's own text and style (null for other tiles)
        presentation: presentationToForm(entity.presentation),
        badge: entity.badge
          ? { label: p(`badge-${entity.badge}`), value: entity.badge }
          : { label: p('badgeNone'), value: null },
      });
      setContentTypes(
        isFilterable(entity.contentType)
          ? await loadContent(entity.contentType.toLowerCase())
          : []
      );
      setContent(entity.contentType);
      await handleContentTypeChange(
        { value: entity?.contentId || null },
        entity.contentType
      );

      if (!cancelled) {
        initializedEntityKeyRef.current = entityInitializationKey;
        setLoading(false);
      }
    };

    initializeEntity();

    return () => {
      cancelled = true;
    };
  }, [
    loadContent,
    contentLoaded,
    entity,
    entityInitializationKey,
    format,
    handleContentTypeChange,
    initialize,
  ]);
  useEffect(() => {
    // Always needed (the company field); content lists load per type
    setContentLoaded(false);
    loadContent('companies').then((options) => {
      setCompanies(options.filter((comp) => comp.value !== null));
      setContentLoaded(true);
    });
  }, [loadContent]);

  const changeContentType = async (contentType) => {
    latestContentType.current = contentType;
    if (isFilterable(contentType)) {
      setLoading(true);
      const options = await loadContent(contentType.toLowerCase());
      // A slower list for a type picked before this one is dropped
      if (latestContentType.current === contentType) {
        setContentTypes(options);
      }
      setLoading(false);
      if (
        [
          'AUCTIONS',
          'NEWS_CATEGORY',
          'PLAYERS',
          'TEAMS',
          'QUESTIONAIRE_CATEGORIES_LEADERBOARD',
          'MATCHES',
          'MATCH_MVPS',
          'GOAL_SPONSOR',
        ].includes(contentType)
      ) {
        change('contentType', null);
      } else {
        change('contentType', {
          label: `${t(`AppLayout.${contentType.toLowerCase()}Tile`)} ${s('list')}`,
          value: null,
        });
      }

      setSelectedEntityAccess({
        groups: null,
        levels: null,
        tiers: null,
        noAccessView: true,
        selectedContent: { type: contentType, contentId: null },
      });
    } else {
      setSelectedEntityAccess({
        groups: null,
        levels: null,
        tiers: null,
        noAccessView: true,
        selectedContent: { type: contentType, contentId: null },
        isEditable: true,
      });
    }
  };
  const handleContentChange = (data) => {
    setContent(data.value);
    change('contentType', null);
    change('subContentType', null);
    changeContentType(data.value);
  };
  const contentTypePlaceholder = (_cont) => {
    switch (_cont) {
      case 'EXTERNAL_LINK':
        return s('externalLink');
      case 'WEBVIEW_LINK':
        return s('webViewLink');
      case 'YOUTUBE_VIDEO':
        return t('NewsForm.youtubeVideoID');
      default:
        return '';
    }
  };
  const entityInitializing =
    !!entity &&
    (!contentLoaded ||
      initializedEntityKeyRef.current !== entityInitializationKey);
  // Report our own readiness up to the page instead of rendering a second,
  // overlapping full-screen <Loading> overlay on top of the page's own one.
  useEffect(() => {
    onLoadingChange(loading || entityInitializing);
  }, [loading, entityInitializing, onLoadingChange]);
  return (
    <form className="form" onSubmit={handleSubmit}>
      <Col md={12} lg={12} xl={12} style={{ marginBottom: '10px', padding: 0 }}>
        <FormBox title={s('details')}>
          <div style={{ padding: '10px' }}>
            <Container className="">
              <Row>
                <Col md={content === 'AUCTIONS' ? 4 : 6}>
                  <div className="form__form-group">
                    <span className="form-header">{p('selectContent')}*</span>
                    <Field
                      name="content"
                      placeholder={p('searchContent')}
                      options={CONTENT_LIST}
                      component={renderSelectField}
                      onChange={handleContentChange}
                      optionRenderer={renderContentOption}
                      filterOptions={filterContentOptions}
                      className="content-select"
                    />
                  </div>
                </Col>
                {/* Image tiles link nowhere: no target to pick */}
                {content === 'IMAGE' ? (
                  <Col md={6}>
                    <div className="form__form-group">
                      <span className="form-header">&nbsp;</span>
                      <small className="form__form-group-description">
                        {p('imageTileInfo')}
                      </small>
                    </div>
                  </Col>
                ) : content !== 'CUSTOM_VIDEO' ? (
                  <Col md={content === 'AUCTIONS' ? 4 : 6}>
                    <div className="form__form-group">
                      <span className="form-header">
                        {p('selectContentType')}*
                      </span>
                      {!isFilterable(content) ? (
                        <Field
                          name="contentType"
                          component={renderField}
                          placeholder={contentTypePlaceholder(content)}
                        />
                      ) : (
                        <Field
                          name="contentType"
                          placeholder={p('selectContentType')}
                          options={contentTypes}
                          component={renderSelectField}
                          disabled={!content}
                          onChange={(e) => {
                            handleContentTypeChange(e, content);
                            change('subContentType', null);
                          }}
                        />
                      )}
                    </div>
                  </Col>
                ) : (
                  <Col md={6}>
                    <div className="form__form-group">
                      <span className="form-header">{s('video')}*</span>
                      <Field
                        name="headingVideo"
                        component={renderDropZoneVideoField}
                      />
                    </div>
                  </Col>
                )}
                {content === 'AUCTIONS' ? (
                  <Col md={content === 'AUCTIONS' ? 4 : 6}>
                    <div className="form__form-group">
                      <span className="form-header">
                        {p('selectContentType')}*
                      </span>
                      <Field
                        disabled={!formValues?.contentType?.value}
                        name="subContentType"
                        placeholder={p('selectContentType')}
                        options={auctionItems}
                        component={renderSelectField}
                      />
                    </div>
                  </Col>
                ) : null}
              </Row>
              <Row>
                <Col md={!showDescription ? 12 : 6}>
                  <div className="form__form-group">
                    {/* A takeover's title is its big line, or the headline */}
                    <span className="form-header">
                      {isGeneric
                        ? p('takeoverHeadline')
                        : isTakeover
                          ? p('takeoverTitle')
                          : s('title')}
                      *
                    </span>
                    <Field
                      name="title"
                      component={renderField}
                      placeholder={
                        isGeneric
                          ? p('takeoverHeadlinePlaceholder')
                          : isTakeover
                            ? p('takeoverTitlePlaceholder')
                            : s('title')
                      }
                    />
                  </div>
                </Col>
                {showDescription ? (
                  <Col md={6}>
                    <div className="form__form-group">
                      <span className="form-header">{bp('description')}*</span>
                      <Field
                        name="description"
                        component={renderField}
                        placeholder={s('description')}
                      />
                    </div>
                  </Col>
                ) : null}
              </Row>
              {isGeneric && (
                <Row>
                  <Col md={12}>
                    <div className="form__form-group">
                      <span className="form-header">{p('takeoverBody')}</span>
                      <Field
                        name="description"
                        component={renderTextAreaField}
                        placeholder={p('takeoverBodyPlaceholder')}
                      />
                      <small className="form__form-group-description">
                        {p('takeoverBodyHint', { max: TAKEOVER_BODY_MAX })}
                      </small>
                    </div>
                  </Col>
                </Row>
              )}
              {(format === 'STORY' || (isTakeover && !isGeneric)) && (
                <Row>
                  <Col md={6}>
                    <div className="form__form-group">
                      {/* A takeover draws it huge, in the light */}
                      <span className="form-header">
                        {p(isTakeover ? 'takeoverHighlight' : 'storyHighlight')}
                        &nbsp;
                        <TooltipWithIcon
                          id="story-highlight-tooltip"
                          text={p(
                            isTakeover
                              ? 'takeoverHighlightInfo'
                              : 'storyHighlightInfo'
                          )}
                        />
                      </span>
                      <Field
                        name="highlight"
                        component={renderField}
                        placeholder={p(
                          isTakeover
                            ? 'takeoverHighlightPlaceholder'
                            : 'storyHighlightPlaceholder'
                        )}
                        maxLength={40}
                      />
                    </div>
                  </Col>
                </Row>
              )}
              <Row>
                <Col>
                  <div className="form__form-group">
                    <span className="form">{s('publicationDate')}</span>
                    <div className="form__form-group-field">
                      <div className="form__form-group-icon">
                        <TimetableIcon />
                      </div>
                      <Field
                        name="publicationDate"
                        component={renderField}
                        type="datetime-local"
                      />
                    </div>
                  </div>
                </Col>
                <Col>
                  <div className="form__form-group">
                    <span className="form">{s('expirationDate')}</span>
                    <div className="form__form-group-field">
                      <div className="form__form-group-icon">
                        <TimetableIcon />
                      </div>
                      <Field
                        name="expirationDate"
                        component={renderField}
                        type="datetime-local"
                      />
                    </div>
                  </div>
                </Col>
              </Row>
              <Row>
                <Col md={6}>
                  <div className="form__form-group">
                    <span className="form-header">
                      {s('sponsor')} - {s('tracking')}
                    </span>
                    <Field
                      name="company"
                      placeholder="Select a partner"
                      options={companies}
                      component={renderSelectField}
                    />
                  </div>
                </Col>
                <Col md={6}>
                  <div className="form__form-group">
                    <span className="form-header">{p('tileBadge')}</span>
                    <Field
                      name="badge"
                      placeholder={p('badgeNone')}
                      options={[
                        { label: p('badgeNone'), value: null },
                        ...TILE_BADGES.map((badge) => ({
                          label: p(`badge-${badge}`),
                          value: badge,
                        })),
                      ]}
                      component={renderSelectField}
                    />
                  </div>
                </Col>
              </Row>
            </Container>
          </div>
        </FormBox>
      </Col>
      {/* The takeover's own text and look (tile.presentation); the title,
          highlight, image and content above are reused */}
      {isTakeover && (
        <Col
          md={12}
          lg={12}
          xl={12}
          style={{ marginBottom: '10px', padding: 0 }}
        >
          <FormBox
            title={
              <>
                {p('takeoverSection')}
                <span className="optional-tag">{s('optional')}</span>
              </>
            }
          >
            <div style={{ padding: '10px' }}>
              <Container>
                <div className="form__form-group">
                  <span className="form-header">{p('takeoverLayout')}</span>
                  <Field
                    name="presentation.layout"
                    component={ChoiceCards}
                    // Not chosen yet = the default, the spotlight
                    format={(value) => value || 'SPOTLIGHT'}
                    ariaLabel={p('takeoverLayout')}
                    options={[
                      {
                        value: 'SPOTLIGHT',
                        icon: SpotlightBeamIcon,
                        label: p('takeoverLayout-SPOTLIGHT'),
                        hint: p('takeoverLayoutHint-SPOTLIGHT'),
                      },
                      {
                        value: 'GENERIC',
                        icon: CardTextOutlineIcon,
                        label: p('takeoverLayout-GENERIC'),
                        hint: p('takeoverLayoutHint-GENERIC'),
                      },
                    ]}
                  />
                </div>
                <Row>
                  <Col md={6}>
                    <div className="form__form-group">
                      <label
                        className="form-header"
                        htmlFor="presentation.eyebrow"
                      >
                        {p('takeoverEyebrow')}
                      </label>
                      <Field
                        name="presentation.eyebrow"
                        component={renderField}
                        placeholder={p('takeoverEyebrowPlaceholder')}
                      />
                    </div>
                  </Col>
                  {!isGeneric && (
                    <Col md={6}>
                      <div className="form__form-group">
                        <label
                          className="form-header"
                          htmlFor="presentation.firstLine"
                        >
                          {p('takeoverFirstLine')}
                        </label>
                        <Field
                          name="presentation.firstLine"
                          component={renderField}
                          placeholder={p('takeoverFirstLinePlaceholder')}
                        />
                      </div>
                    </Col>
                  )}
                </Row>
                {!isGeneric && (
                  <div
                    className="form__form-group"
                    role="group"
                    aria-labelledby="takeover-facts-label"
                  >
                    <span className="form-header" id="takeover-facts-label">
                      {p('takeoverFacts')}
                    </span>
                    <Row>
                      {[0, 1, 2].map((index) => (
                        <Col md={4} key={index}>
                          <label
                            className="sr-only"
                            htmlFor={`presentation.facts[${index}]`}
                          >
                            {p('takeoverFact', { number: index + 1 })}
                          </label>
                          <Field
                            name={`presentation.facts[${index}]`}
                            component={renderField}
                            placeholder={p(
                              `takeoverFactPlaceholder${index + 1}`
                            )}
                          />
                        </Col>
                      ))}
                    </Row>
                  </div>
                )}
                <Row>
                  <Col md={6}>
                    <div className="form__form-group">
                      <label
                        className="form-header"
                        htmlFor="presentation.actionLabel"
                      >
                        {p('takeoverActionLabel')}
                      </label>
                      <Field
                        name="presentation.actionLabel"
                        component={renderField}
                        placeholder={p('takeoverActionLabelPlaceholder')}
                      />
                      <small className="form__form-group-description">
                        {p('takeoverActionLabelHint')}
                      </small>
                    </div>
                  </Col>
                </Row>
                <Row>
                  <Col md={6}>
                    <div className="form__form-group">
                      <label
                        className="form-header"
                        htmlFor="presentation.moreLabel"
                      >
                        {p('takeoverMoreLabel')}
                      </label>
                      <Field
                        name="presentation.moreLabel"
                        component={renderField}
                        placeholder={p('takeoverMoreLabelPlaceholder')}
                      />
                    </div>
                  </Col>
                  <Col md={6}>
                    <div className="form__form-group">
                      <label
                        className="form-header"
                        htmlFor="presentation.moreLink"
                      >
                        {p('takeoverMoreLink')}
                      </label>
                      <Field
                        name="presentation.moreLink"
                        component={renderField}
                        type="url"
                        placeholder="https://"
                      />
                    </div>
                  </Col>
                </Row>
                {!isGeneric && (
                  <>
                    <div className="form__form-group">
                      <span className="form-header">
                        {p('takeoverImageStyle')}
                      </span>
                      <Field
                        name="presentation.imageStyle"
                        component={ChoiceCards}
                        // Not chosen yet = the default, the cut-out
                        format={(value) => value || 'CUTOUT'}
                        ariaLabel={p('takeoverImageStyle')}
                        options={[
                          {
                            value: 'CUTOUT',
                            icon: CutoutIcon,
                            label: p('takeoverImageStyle-CUTOUT'),
                            hint: p('takeoverImageStyleHint-CUTOUT'),
                          },
                          {
                            value: 'CARD',
                            icon: CardIcon,
                            label: p('takeoverImageStyle-CARD'),
                            hint: p('takeoverImageStyleHint-CARD'),
                          },
                        ]}
                      />
                      <small className="form__form-group-description">
                        {p('takeoverImageStyleInfo')}
                      </small>
                    </div>
                    <div className="form__form-group mb-0">
                      <span className="form-header">
                        {p('takeoverLightColor')}
                      </span>
                      <Field
                        name="presentation.lightColor"
                        component={OptionalColorField}
                        label={p('takeoverLightColor')}
                        clearLabel={p('clearColor')}
                        emptyLabel={p('takeoverLightFromClub')}
                        presets={LIGHT_PRESETS}
                      />
                      <small className="form__form-group-description">
                        {p('takeoverLightColorHint')}
                      </small>
                    </div>
                  </>
                )}
              </Container>
            </div>
          </FormBox>
        </Col>
      )}
      <Col md={12} lg={12} xl={12} style={{ marginBottom: '10px', padding: 0 }}>
        <FormBox
          title={`${s('image')} ${content === 'YOUTUBE_VIDEO' || format === 'TEXT_BANNER' ? `(${s('optional')})` : ''}`}
        >
          <Col>
            <div className="form__form-group">
              <Field
                saveBase64Cropped
                name="headingImage"
                maxSize={3}
                component={renderDropZoneField}
                crop={getCropType(imageFormat, imageSize)}
                displayCrop
              />
              <span className="form-header">
                <strong>{getReadableFormat(imageFormat)}</strong>{' '}
                {/* The image-and-text takeover fills the top with a photo */}
                {isGeneric
                  ? p('takeoverGenericImageHint')
                  : getFormatDimensions(imageFormat, true, imageSize)}
              </span>
            </div>
          </Col>
        </FormBox>
      </Col>
    </form>
  );
};

const reduxF = reduxForm({
  form: 'tile_form',
  validate,
});

const mapStateToProps = (state) => ({
  formValues: getFormValues('tile_form')(state),
});
export default connect(mapStateToProps)(reduxF(SpotlightTileForm));
